import React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { readGlobalProfiles } from '@services/firebaseGlobal.js';
import { readUserCollection, deleteUserDocument } from '@services/firebaseUser.js';
import { updateUserData } from '@services/firebaseGlobal.js';
import { getFirebase } from '@services/firebase.js';
import { FaTimes, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useAlert } from '../contexts/ToastContext';

let db = null; // Lazy initialization

const getDb = () => {
  if (!db) {
    ({ db } = getFirebase());
  }
  return db;
};

// Debounce function to limit rapid click events
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Define styles directly within the script to match UserAdmin.jsx
const popupStyles = `
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .popup-content {
    background: white;
    border-radius: 8px;
    width: 85vw;
    max-width: 600px;
    min-height: 250px;
    max-height: 80vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 10px;
    position: relative;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
  }

  .popup-content::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 600px) {
    .popup-content {
      width: 90vw;
      max-width: 90vw;
      padding: 5px;
      min-height: 200px;
    }

    .popup-body .user-table th,
    .popup-body .user-table td {
      padding: 3px;
      font-size: 10px;
    }

    .popup-body .user-table th:nth-child(1),
    .popup-body .user-table td:nth-child(1) {
      width: 25px;
    }

    .popup-body .user-table th:nth-child(2),
    .popup-body .user-table td:nth-child(2) {
      width: 70px;
    }

    .popup-body .user-table th:nth-child(3),
    .popup-body .user-table td:nth-child(3) {
      width: 70px;
    }

    .popup-body .user-table th:nth-child(4),
    .popup-body .user-table td:nth-child(4) {
      width: 100px;
    }

    .popup-body .user-table th:nth-child(5),
    .popup-body .user-table td:nth-child(5) {
      width: 70px;
    }

    .use-in-section label {
      font-size: 12px;
    }

    .use-in-section select {
      padding: 3px;
      font-size: 12px;
    }

    .action-btn.delete-btn {
      padding: 6px 12px;
      font-size: 12px;
    }

    .filter-input {
      padding: 2px;
      font-size: 10px;
    }

    .search-clear-btn {
      font-size: 10px;
      padding: 1px;
    }

    .no-match-message {
      font-size: 10px;
    }
  }

  .popup-close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    color: #666;
  }

  .popup-close-btn:hover {
    color: #333;
  }

  .popup-body {
    margin-bottom: 10px;
    flex: 1;
  }

  .filter-section {
    display: flex;
    gap: 5px;
    margin-bottom: 5px;
    width: 50%;
    margin: 10px auto 5px;
    position: relative;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    background: #fff;
  }

  .filter-input {
    padding: 4px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 12px;
    flex: 1;
  }

  .search-clear-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    font-size: 12px;
    padding: 4px;
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
  }

  .search-clear-btn:hover {
    color: #333;
  }

  .table-container {
    overflow-x: auto;
    max-width: 100%;
    margin-top: 32px;
  }

  .popup-body .user-table {
    width: 100%;
    min-width: 600px;
    border-collapse: collapse;
    table-layout: fixed;
    background-color: #fff;
  }

  .popup-body .user-table th,
  .popup-body .user-table td {
    padding: 6px;
    border: 1px solid #ddd;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 1.2;
  }

  .popup-body .user-table th:nth-child(1),
  .popup-body .user-table td:nth-child(1) {
    width: 50px;
  }

  .popup-body .user-table th:nth-child(2),
  .popup-body .user-table td:nth-child(2) {
    width: 150px;
  }

  .popup-body .user-table th:nth-child(3),
  .popup-body .user-table td:nth-child(3) {
    width: 150px;
  }

  .popup-body .user-table th:nth-child(4),
  .popup-body .user-table td:nth-child(4) {
    width: 200px;
  }

  .popup-body .user-table th:nth-child(5),
  .popup-body .user-table td:nth-child(5) {
    width: 150px;
  }

  .popup-body .user-table th {
    background-color: #f4f4f4;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .popup-body .user-table td {
    background-color: #fff;
    color: #555;
  }

  .sort-icon {
    display: inline-block;
    margin-left: 3px;
    cursor: pointer;
    vertical-align: middle;
    font-size: 12px;
  }

  .popup-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .use-in-section {
    display: flex;
    align-items: center;
    gap: 10px;
    white-space: nowrap;
  }

  .use-in-section label {
    font-weight: bold;
    display: inline-block;
    margin: 0;
  }

  .use-in-section select {
    padding: 5px;
    border-radius: 4px;
    border: 1px solid #ddd;
    display: inline-block;
    font-size: 12px;
  }

  .popup-delete-btn {
    background-color: #fa4848 !important;
    color: white !important;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    margin-top: 8px;
  }

  .popup-delete-btn:disabled {
    background-color: #fa4848 !important;
    color: white !important;
    opacity: 0.6;
    cursor: not-allowed;
  }

  .action-btn.delete-btn {
    background-color: #fa4848 !important;
    color: white !important;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    width: auto !important;
    height: auto !important;
    display: inline-block !important;
    font-size: 13px !important;
  }

  .action-btn.delete-btn:disabled {
    background-color: #fa4848 !important;
    color: white !important;
    opacity: 0.6;
    cursor: not-allowed;
  }

  .popup-loading,
  .popup-error,
  .no-usage-message,
  .no-match-message {
    text-align: center;
    padding: 20px;
    color: #666;
    width: 100%;
    min-height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 12px;
  }

  .popup-error {
    color: red;
  }

  .confirm-dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 300px;
    text-align: center;
  }

  .confirm-dialog p {
    margin: 0;
    font-size: 14px;
    color: #333;
  }

  .confirm-dialog-buttons {
    display: flex;
    justify-content: center;
    gap: 10px;
  }

  .confirm-btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
  }

  .confirm-btn.confirm {
    background-color: #ff4d4f;
    color: white;
  }

  .confirm-btn.cancel {
    background-color: #ccc;
    color: #333;
  }

  .confirm-btn:disabled {
    background-color: #ddd;
    cursor: not-allowed;
  }

  html[data-theme="dark"] .popup-content {
    background: #2b2b2b;
    color: #ececec;
    box-shadow: 0 2px 10px rgba(0,0,0,0.5);
  }

  html[data-theme="dark"] .popup-close-btn {
    color: #aaa;
  }

  html[data-theme="dark"] .popup-close-btn:hover {
    color: #ececec;
  }

  html[data-theme="dark"] .filter-section {
    background: #222;
    box-shadow: 0 2px 4px rgba(0,0,0,0.4);
  }

  html[data-theme="dark"] .filter-input {
    background: #1e1e1e;
    border-color: #3a3a3a;
    color: #ececec;
  }

  html[data-theme="dark"] .search-clear-btn {
    color: #aaa;
  }

  html[data-theme="dark"] .popup-body .user-table {
    background-color: #2b2b2b;
  }

  html[data-theme="dark"] .popup-body .user-table th,
  html[data-theme="dark"] .popup-body .user-table td {
    border-color: #3a3a3a;
  }

  html[data-theme="dark"] .popup-body .user-table th {
    background-color: #333;
    color: #ececec;
  }

  html[data-theme="dark"] .popup-body .user-table td {
    background-color: #2b2b2b;
    color: #ccc;
  }

  html[data-theme="dark"] .popup-body .user-table tr:hover td {
    background-color: #333;
  }

  html[data-theme="dark"] .use-in-section label {
    color: #ccc;
  }

  html[data-theme="dark"] .use-in-section select {
    background: #1e1e1e;
    border-color: #3a3a3a;
    color: #ececec;
  }

  html[data-theme="dark"] .popup-loading,
  html[data-theme="dark"] .no-usage-message,
  html[data-theme="dark"] .no-match-message {
    color: #aaa;
  }

  html[data-theme="dark"] .popup-error {
    color: #ff8080;
  }

  html[data-theme="dark"] .confirm-dialog {
    background: #2b2b2b;
    box-shadow: 0 2px 12px rgba(0,0,0,0.6);
  }

  html[data-theme="dark"] .confirm-dialog p {
    color: #ececec;
  }

  html[data-theme="dark"] .confirm-btn.cancel {
    background-color: #444;
    color: #ccc;
  }

  html[data-theme="dark"] .confirm-btn:disabled {
    background-color: #3a3a3a;
    color: #666;
  }
`;

const ProfileUsagePopup = ({ profileId, onClose, onDeleteProfile, userRole }) => {
  const showAlert = useAlert();
  if (typeof React === 'undefined') {
    console.error('React is not available in ProfileUsagePopup');
    return null;
  }
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [useIn, setUseIn] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasUsage, setHasUsage] = useState(true);
  const [usedAreasForSelected, setUsedAreasForSelected] = useState(['All']);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(null); // null, 'direct', or 'users'
  const timerRef = useRef({}); // Track active timers
  const popupContentRef = useRef(null);

  // Debounced handlers to prevent rapid clicks
  const handleDeleteProfileDirectly = useCallback(
    debounce((e) => {
      e.stopPropagation(); // Prevent click from bubbling to parent
      const timerLabel = `handleDeleteProfileDirectly_${profileId}`;
      if (timerRef.current[timerLabel]) {
        console.timeEnd(timerRef.current[timerLabel]);
      }
      timerRef.current[timerLabel] = timerLabel;
      console.time(timerLabel);
      setShowConfirm('direct');
      console.timeLog(timerLabel, 'After opening confirm dialog');
    }, 100),
    [profileId]
  );

  const handleDelete = useCallback(
    debounce((e) => {
      e.stopPropagation(); // Prevent click from bubbling to parent
      if (selectedUsers.length === 0) {
        showAlert('Please select at least one user to delete.');
        return;
      }
      const timerLabel = `handleDelete_${profileId}`;
      if (timerRef.current[timerLabel]) {
        console.timeEnd(timerRef.current[timerLabel]);
      }
      timerRef.current[timerLabel] = timerLabel;
      console.time(timerLabel);
      setShowConfirm('users');
      console.timeLog(timerLabel, 'After opening confirm dialog');
    }, 100),
    [profileId, selectedUsers]
  );

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    if (showConfirm === 'direct') {
      const timerLabel = `handleDeleteProfileDirectly_${profileId}`;
      onDeleteProfile()
        .then(() => {
          setShowConfirm(null);
          onClose();
        })
        .catch((err) => {
          console.error('Error deleting profile:', err.message, err.stack);
          showAlert(`Failed to delete profile: ${err.message}`);
        })
        .finally(() => {
          setIsDeleting(false);
          setShowConfirm(null);
          if (timerRef.current[timerLabel]) {
            console.timeEnd(timerLabel);
            timerRef.current[timerLabel] = null;
          }
        });
    } else if (showConfirm === 'users') {
      const timerLabel = `handleDelete_${profileId}`;
      let collectionsToDelete = [];
      if (useIn === 'All') {
        const areasSet = new Set();
        selectedUsers.forEach((userId) => {
          const user = users.find((u) => u.id === userId);
          if (user) {
            const { usageFlags, orderCount } = user;
            if (usageFlags.inCustom) areasSet.add('custom');
            if (usageFlags.inFavourites) areasSet.add('favourites');
            if (usageFlags.inEnquiry) areasSet.add('enquiry');
            if (orderCount > 0) areasSet.add('order');
          }
        });
        collectionsToDelete = Array.from(areasSet).filter((area) => area !== 'order');
      } else {
        collectionsToDelete = [useIn.toLowerCase()];
      }
      let anyDocsDeleted = false;
      Promise.all(
        selectedUsers.map(async (userId) => {
          for (const collection of collectionsToDelete) {
            const userDocs = await readUserCollection(collection, userId);
            const docsToDelete = userDocs.filter((doc) => doc.globalId === profileId);
            for (const doc of docsToDelete) {
              await deleteUserDocument(collection, userId, doc.id);
              anyDocsDeleted = true;
            }
          }
          if (useIn === 'All' || useIn === 'Order') {
            const user = users.find((u) => u.id === userId);
            if (user && user.orderCount > 0) {
              await updateUserData('profile', userId, { orderCount: 0 });
              anyDocsDeleted = true;
            }
          }
        })
      )
        .then(() => {
          setSelectedUsers([]);
          setSelectAll(false);
          updateUsedAreasForSelected([]);
          return getDoc(doc(getDb(), 'profileUsage', profileId));
        })
        .then((usageDoc) => {
          if (usageDoc.exists()) {
            const data = usageDoc.data();
            const usedBy = data.usedBy || [];
            if (usedBy.length === 0) {
              setHasUsage(false);
              setUsers([]);
              setSelectedUsers([]);
              setSelectAll(false);
              setUsedAreasForSelected(['All']);
              setUseIn('All');
            } 
          } else {
            setHasUsage(false);
            setUsers([]);
            setSelectedUsers([]);
            setSelectAll(false);
            setUsedAreasForSelected(['All']);
            setUseIn('All');
          }

          if (popupContentRef.current) {
            popupContentRef.current.focus();
          }
        })
        .catch((err) => {
          console.error('Error deleting profile usage:', err.message, err.stack);
          showAlert(`Failed to delete profile usage: ${err.message}`);
        })
        .finally(() => {
          setIsDeleting(false);
          setShowConfirm(null);
          if (timerRef.current[timerLabel]) {
            console.timeEnd(timerLabel);
            timerRef.current[timerLabel] = null;
          }
        });
    }
  };

  const handleCancelDelete = () => {
    setShowConfirm(null);
    if (showConfirm === 'direct') {
      const timerLabel = `handleDeleteProfileDirectly_${profileId}`;
      if (timerRef.current[timerLabel]) {
        console.timeEnd(timerLabel);
        timerRef.current[timerLabel] = null;
      }
    } else if (showConfirm === 'users') {
      const timerLabel = `handleDelete_${profileId}`;
      if (timerRef.current[timerLabel]) {
        console.timeEnd(timerLabel);
        timerRef.current[timerLabel] = null;
      }
    }
  };

  const formatElapsedTime = (lastUsed) => {
    if (!lastUsed) return 'Never used';
    const lastUsedDate = new Date(lastUsed);
    const now = new Date();
    const diffInMs = now - lastUsedDate;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInHours < 24) {
      return `${diffInHours} Hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 90) {
      return `${diffInDays} Day${diffInDays !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 365) {
      const diffInMonths = Math.floor(diffInDays / 30);
      return `${diffInMonths} Month${diffInMonths !== 1 ? 's' : ''} ago`;
    } else {
      const diffInYears = Math.floor(diffInDays / 365);
      return `${diffInYears} Year${diffInYears !== 1 ? 's' : ''} ago`;
    }
  };

  useEffect(() => {
    const firebaseDb = getDb();
    if (!firebaseDb || !profileId) {
      setError('Firestore or profile ID is not available');
      setLoading(false);
      return;
    }

    const usageDocRef = doc(firebaseDb, 'profileUsage', profileId);
    const unsubscribe = onSnapshot(
      usageDocRef,
      async (docSnapshot) => {
        try {
          setError(null);
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            const usedBy = data.usedBy || [];
            if (usedBy.length === 0) {
              setHasUsage(false);
              setLoading(false);
              return;
            }
            setHasUsage(true);
            const usageFlags = data.usageFlags || {};

            const userDetails = await Promise.all(
              usedBy.map(async (userId) => {
                const cUserDataList = await readGlobalProfiles('cUsers');
                const cUserData = cUserDataList.find((u) => u.id === userId) || {};
                const profileDataList = await readUserCollection('profile', userId);
                const profileData = profileDataList.length > 0 ? profileDataList[0] : null;

                const usage = usageFlags[userId] || {};
                const usedAreas = [];
                if (usage.inCustom) usedAreas.push('Custom');
                if (usage.inFavourites) usedAreas.push('Favourites');
                if (usage.inEnquiry) usedAreas.push('Enquiry');
                if (usage.inOrder) usedAreas.push('Order');
                const orderCount = profileData?.orderCount || 0;
                //if (orderCount > 0) usedAreas.push('Order');

                const lastUsedAt = usage.lastUsedAt ? new Date(usage.lastUsedAt.seconds * 1000) : null;
                const formattedLastUsed = lastUsedAt ? formatElapsedTime(lastUsedAt) : 'Never used';

                return {
                  id: userId,
                  name: profileData?.f_name || cUserData.f_name || '',
                  surname: profileData?.l_name || cUserData.l_name || '',
                  email: profileData?.email || cUserData.email || '',
                  usedAreas: usedAreas.join(', ') || 'None',
                  usageFlags: usage,
                  orderCount,
                  lastUsedAt: lastUsedAt ? lastUsedAt.getTime() : 0,
                  formattedLastUsed,
                };
              })
            );

            setUsers(userDetails);
            setFilteredUsers(userDetails);
          } else {
            setHasUsage(false);
          }
          setLoading(false);
        } catch (err) {
          console.error('Error fetching profile usage:', err.message, err.stack);
          setError('Failed to load profile usage data');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error listening to profileUsage:', err.message, err.stack);
        setError(
          err.message.includes('net::ERR_BLOCKED_BY_CLIENT') || err.message.includes('blocked')
            ? 'Failed to load profile usage data. This may be due to an ad blocker or privacy extension blocking Firestore requests. Please whitelist firestore.googleapis.com or disable your ad blocker.'
            : 'Failed to load profile usage data: ' + err.message
        );
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [profileId]);

  useEffect(() => {
  }, [loading, error, hasUsage]);

  useEffect(() => {
    let filtered = [...users];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(query) ||
          user.surname.toLowerCase().includes(query) ||
          (user.email && user.email.toLowerCase().includes(query))
      );
    }
    filtered.sort((a, b) => (sortOrder === 'asc' ? a.lastUsedAt - b.lastUsedAt : b.lastUsedAt - a.lastUsedAt));
    setFilteredUsers(filtered);
    setSelectedUsers((prev) => prev.filter((userId) => filtered.some((user) => user.id === userId)));
    setSelectAll(false);
    updateUsedAreasForSelected([]);
  }, [users, searchQuery, sortOrder]);

  const handleCheckboxChange = (userId) => {
    setSelectedUsers((prev) => {
      const newSelected = prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId];
      updateUsedAreasForSelected(newSelected);
      return newSelected;
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedUsers([]);
      setUsedAreasForSelected(['All']);
    } else {
      const allUserIds = filteredUsers.map((user) => user.id);
      setSelectedUsers(allUserIds);
      updateUsedAreasForSelected(allUserIds);
    }
    setSelectAll(!selectAll);
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleClearSearch = () => setSearchQuery('');

  const updateUsedAreasForSelected = (selected) => {
    if (selected.length === 0) {
      setUsedAreasForSelected(['All']);
      setUseIn('All');
      return;
    }
    const areasSet = new Set();
    selected.forEach((userId) => {
      const user = users.find((u) => u.id === userId);
      if (user) {
        const { usageFlags, orderCount } = user;
        if (usageFlags.inCustom) areasSet.add('Custom');
        if (usageFlags.inFavourites) areasSet.add('Favourites');
        if (usageFlags.inEnquiry) areasSet.add('Enquiry');
        if (usageFlags.inOrder) areasSet.add('Order');
        //if (orderCount > 0) areasSet.add('Order');
      }
    });
    const areas = Array.from(areasSet);
    if (areas.length === 0) {
      setUsedAreasForSelected(['All']);
      setUseIn('All');
    } else if (areas.length === 1) {
      setUsedAreasForSelected(areas);
      setUseIn(areas[0]);
    } else {
      setUsedAreasForSelected(['All', ...areas]);
      setUseIn('All');
    }
  };

  const handleSort = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  const popupContent = (
    <>
      <div className="popup-overlay" onClick={handleOverlayClick}>
        <style>{popupStyles}</style>
        <div className="popup-content" onClick={handleContentClick} ref={popupContentRef} tabIndex={-1}>
          <button className="popup-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
          <div className="popup-body">
            {loading ? (
              <div className="popup-loading">Loading...</div>
            ) : error ? (
              <div className="popup-error">{error}</div>
            ) : !hasUsage ? (
              <div className="no-usage-message">
                <p>No users are currently using this profile.</p>
                <button
                  className="popup-delete-btn"
                  onClick={handleDeleteProfileDirectly}
                  disabled={isDeleting || showConfirm}
                >
                  Delete Profile
                </button>
              </div>
            ) : (
              <>
                <div className="filter-section">
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="Search by Name, Surname, or Email..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {searchQuery && (
                    <button className="search-clear-btn" onClick={handleClearSearch}>
                      <FaTimes />
                    </button>
                  )}
                </div>
                <div className="table-container">
                  <table className="user-table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAllChange}
                          />
                        </th>
                        <th>Name</th>
                        <th>Surname</th>
                        <th>Email</th>
                        <th>
                          Used
                          <span className="sort-icon" onClick={handleSort}>
                            {sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleCheckboxChange(user.id)}
                            />
                          </td>
                          <td>{user.name}</td>
                          <td>{user.surname}</td>
                          <td>{user.email}</td>
                          <td>{user.formattedLastUsed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredUsers.length === 0 && <p className="no-match-message">No users match the current search.</p>}
              </>
            )}
          </div>
          {!loading && !error && hasUsage && (
            <div className="popup-footer">
              <div className="use-in-section">
                <label>Use-In: </label>
                <select value={useIn} onChange={(e) => setUseIn(e.target.value)}>
                  {usedAreasForSelected.map((area) => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
              {userRole === 'Admin' && (
                <button
                  className="popup-delete-btn"
                  onClick={handleDelete}
                  disabled={selectedUsers.length === 0 || isDeleting || showConfirm}
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {showConfirm && (
        <div className="confirm-dialog">
          <p>
            {showConfirm === 'direct'
              ? 'Are you sure you want to delete this profile? This action cannot be undone.'
              : `Are you sure you want to delete the profile from ${useIn} for the selected users? This action cannot be undone.`}
          </p>
          <div className="confirm-dialog-buttons">
            <button
              className="confirm-btn confirm"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              Confirm
            </button>
            <button
              className="confirm-btn cancel"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(popupContent, document.getElementById('portal-root'));
};

export default React.memo(ProfileUsagePopup);